/**
 * ExerciseItemsPanel
 * 
 * Allows users to add items to a Quick Exercise.
 * Unlike ArrangeItemPoolPanel, this panel lets users:
 * 1. Search and add existing global items to the exercise
 * 2. Create new items directly for the exercise
 * 
 * This solves the "empty cycle" problem - where ArrangeItemPoolPanel
 * only shows items already in the cycle, but new exercises have no items.
 */

import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  Plus,
  Search,
  Package,
  Trash2,
  CheckCircle,
  Layers,
  X,
} from 'lucide-react';
import { AltitudeIndicator } from '@/components/ui/AltitudeIndicator';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useCycleParticipationStore } from '@/stores/prioritization/cycleParticipationStore';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { validatePoolHomogeneity, getHomogeneitySummary } from '@/lib/poolHomogeneityValidator';
import type { Candidate } from '@/types/prioritization/lifecycle';
import type { AltitudeLevel } from '@/types/prioritization/itemLifecycle';

interface ExerciseItemsPanelProps {
  exerciseId: string;
}

export function ExerciseItemsPanel({ exerciseId }: ExerciseItemsPanelProps) {
  const userId = useUserStore(state => state.userId) || 'anonymous';
  
  // State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  
  // New candidate form
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newType, setNewType] = useState<string>('');
  const [newAltitude, setNewAltitude] = useState<number | undefined>();
  
  // Stores
  const items = useItemsStore(state => state.items);
  const createItem = useItemsStore(state => state.createItem);
  
  // Subscribe to participations state directly (not just the selector function)
  // This ensures re-render when participations change
  const participations = useCycleParticipationStore(state => state.participations);
  const addItemToCycle = useCycleParticipationStore(state => state.addItemToCycle);
  const removeItemFromCycle = useCycleParticipationStore(state => state.removeItemFromCycle);
  
  const addItemsToCycle = useDegapProcessStore(state => state.addItemsToCycle);
  const removeItemsFromCycle = useDegapProcessStore(state => state.removeItemsFromCycle);
  
  // Get items currently in exercise
  const exerciseItemIds = useMemo(() => {
    // Filter participations for this exercise
    return Object.values(participations)
      .filter(p => p.cycleId === exerciseId && p.cycleStatus !== 'excluded')
      .map(p => p.itemId);
  }, [exerciseId, participations]);
  
  const exerciseItems = useMemo(() => {
    return items.filter(c => exerciseItemIds.includes(c.id));
  }, [items, exerciseItemIds]);
  
  // Pool homogeneity check
  const homogeneityResult = useMemo(() => {
    if (exerciseItems.length === 0) return null;
    return validatePoolHomogeneity(exerciseItems);
  }, [exerciseItems]);
  
  // Get available candidates (not already in exercise)
  const availableCandidates = useMemo(() => {
    return items.filter(c => !exerciseItemIds.includes(c.id));
  }, [items, exerciseItemIds]);
  
  // Filtered candidates for search
  const filteredCandidates = useMemo(() => {
    if (!searchQuery) return availableCandidates;
    const query = searchQuery.toLowerCase();
    return availableCandidates.filter(c =>
      c.title.toLowerCase().includes(query) ||
      c.description?.toLowerCase().includes(query)
    );
  }, [availableCandidates, searchQuery]);
  
  // Add existing candidates to exercise
  const handleAddToExercise = useCallback(async () => {
    for (const itemId of selectedIds) {
      // Add to cycle_item_participation (synchronous, syncs to Supabase internally)
      addItemToCycle({ cycleId: exerciseId, itemId, reason: 'manual_selection' });
      // Also update degapProcessStore's itemIds array (async)
      await addItemsToCycle(exerciseId, [itemId]);
    }
    setSelectedIds([]);
    setShowAddModal(false);
  }, [selectedIds, exerciseId, addItemToCycle, addItemsToCycle]);
  
  // Remove item from exercise
  const handleRemoveFromExercise = useCallback(async (itemId: string) => {
    // Remove from cycleParticipationStore (synchronous)
    removeItemFromCycle(exerciseId, itemId);
    // Remove from degapProcessStore's itemIds (async)
    await removeItemsFromCycle(exerciseId, [itemId]);
  }, [exerciseId, removeItemFromCycle, removeItemsFromCycle]);
  
  // Create new candidate and add to exercise
  const handleCreateCandidate = useCallback(async () => {
    if (!newTitle.trim()) return;
    
    try {
      // Pre-generate ID so we can reference it immediately
      const newId = uuidv4();
      
      // Create the item using the store method
      createItem({
        id: newId,
        title: newTitle.trim(),
        description: newDescription.trim() || undefined,
        itemCategory: newType as Candidate['itemCategory'] || undefined,
        altitudeLevel: newAltitude as AltitudeLevel | undefined,
        createdBy: userId,
        tags: [],
      });
      
      // Add to exercise (small delay to ensure store update propagates)
      setTimeout(async () => {
        addItemToCycle({ cycleId: exerciseId, itemId: newId, reason: 'manual_selection' });
        await addItemsToCycle(exerciseId, [newId]);
      }, 100);
      
      // Reset form
      setNewTitle('');
      setNewDescription('');
      setNewType('');
      setNewAltitude(undefined);
      setShowCreateModal(false);
    } catch (error) {
      console.error('[ExerciseItemsPanel] Failed to create candidate:', error);
    }
  }, [
    newTitle, 
    newDescription, 
    newType, 
    newAltitude, 
    userId, 
    exerciseId, 
    createItem, 
    addItemToCycle, 
    addItemsToCycle
  ]);
  
  // Toggle selection in add modal
  const toggleSelection = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Package className="h-5 w-5 text-[var(--text-ter)]" />
            <div>
              <h3 className="font-semibold text-[var(--text-pri)]">Exercise Items</h3>
              <p className="text-sm text-[var(--text-ter)]">
                {exerciseItems.length} item{exerciseItems.length !== 1 ? 's' : ''} in this exercise
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-dim)] text-[var(--text-sec)] hover:bg-[var(--slate-dim)]"
            >
              <Plus className="h-4 w-4" />
              Add Existing
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)]"
            >
              <Plus className="h-4 w-4" />
              Create New
            </button>
          </div>
        </div>
        
        {/* Homogeneity Status */}
        {homogeneityResult && (
          <div className={`p-3  mb-4 ${
            homogeneityResult.isHomogeneous 
              ? 'bg-[var(--green-dim)] border border-[var(--green)]' 
              : 'bg-[var(--mustard-dim)] border border-[var(--mustard)]'
          }`}>
            <div className="flex items-center gap-2">
              <Layers className={`h-4 w-4 ${
                homogeneityResult.isHomogeneous ? 'text-[var(--green)]' : 'text-[var(--mustard)]'
              }`} />
              <span className={`text-sm font-medium ${
                homogeneityResult.isHomogeneous ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'
              }`}>
                {homogeneityResult.isHomogeneous ? 'Pool is homogeneous' : 'Pool contains mixed items'}
              </span>
            </div>
            <p className={`text-xs mt-1 ${
              homogeneityResult.isHomogeneous ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'
            }`}>
              {getHomogeneitySummary(homogeneityResult)}
            </p>
          </div>
        )}
        
        {/* Items List */}
        {exerciseItems.length > 0 ? (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {exerciseItems.map(item => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 bg-[var(--surface)]"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <CheckCircle className="h-4 w-4 text-[var(--green)] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-[var(--text-pri)] truncate">{item.title}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {item.itemCategory && (
                        <span className="text-xs px-1.5 py-0.5 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded">
                          {item.itemCategory}
                        </span>
                      )}
                      {item.altitudeLevel !== undefined && (
                        <AltitudeIndicator level={item.altitudeLevel} category={item.itemCategory} showLabel={false} className="ml-0.5" />
                      )}
                      {!item.itemCategory && (
                        <span className="text-xs px-1.5 py-0.5 bg-[var(--coral-dim)] text-[var(--coral)] rounded">
                          No type
                        </span>
                      )}
                      {item.altitudeLevel === undefined && (
                        <span className="text-xs px-1.5 py-0.5 bg-[var(--coral-dim)] text-[var(--coral)] rounded">
                          No altitude
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Link
                    to={`/prioritization/scoring/${item.id}`}
                    className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] px-2 py-1"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => handleRemoveFromExercise(item.id)}
                    className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded"
                    title="Remove from exercise"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-[var(--surface)]">
            <Package className="h-10 w-10 text-[var(--text-ter)] mx-auto mb-3" />
            <p className="text-[var(--text-sec)] font-medium">No items yet</p>
            <p className="text-sm text-[var(--text-ter)] mt-1">
              Add existing candidates or create new ones to get started.
            </p>
          </div>
        )}
      </div>
      
      {/* Add Existing Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddModal(false)} />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-[var(--bg-raised)] shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <div>
                  <h2 className="text-lg font-semibold">Add Existing Candidates</h2>
                  <p className="text-sm text-[var(--text-ter)]">
                    Select candidates to add to this exercise
                  </p>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-[var(--hover-tint)]"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Search */}
              <div className="p-4 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
                  <input
                    type="text"
                    placeholder="Search candidates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border"
                  />
                </div>
                <div className="mt-2 text-sm text-[var(--text-ter)]">
                  {filteredCandidates.length} candidate{filteredCandidates.length !== 1 ? 's' : ''} available
                </div>
              </div>
              
              {/* List */}
              <div className="flex-1 overflow-y-auto p-4">
                {filteredCandidates.length === 0 ? (
                  <div className="text-center py-8 text-[var(--text-ter)]">
                    {availableCandidates.length === 0 
                      ? 'No candidates available. Create new ones instead.'
                      : 'No candidates match your search.'}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredCandidates.map(candidate => (
                      <button
                        key={candidate.id}
                        onClick={() => toggleSelection(candidate.id)}
                        className={`w-full p-3  border-2 text-left transition-all ${
                          selectedIds.includes(candidate.id)
                            ? 'border-[var(--slate)] bg-[var(--slate-dim)]'
                            : 'border-[var(--rule)] hover:border-[var(--rule)]'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedIds.includes(candidate.id)
                              ? 'border-[var(--slate)] bg-[var(--slate)]'
                              : 'border-[var(--rule)]'
                          }`}>
                            {selectedIds.includes(candidate.id) && (
                              <CheckCircle className="h-4 w-4 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-[var(--text-pri)] truncate">
                              {candidate.title}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              {candidate.itemCategory && (
                                <span className="text-xs px-1.5 py-0.5 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded">
                                  {candidate.itemCategory}
                                </span>
                              )}
                              {candidate.altitudeLevel !== undefined && (
                                <AltitudeIndicator level={candidate.altitudeLevel} category={candidate.itemCategory} showLabel={false} className="ml-0.5" />
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="p-4 border-t flex justify-between">
                <span className="text-sm text-[var(--text-ter)]">
                  {selectedIds.length} selected
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddToExercise}
                    disabled={selectedIds.length === 0}
                    className="px-4 py-2 text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50"
                  >
                    Add Selected
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Create New Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowCreateModal(false)} />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-[var(--bg-raised)] shadow-xl max-w-lg w-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold">New item</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-[var(--hover-tint)]"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Form */}
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Title <span className="text-[var(--coral)]">*</span>
                  </label>
                  <input
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="Enter item title..."
                    className="w-full px-3 py-2 border"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Description
                  </label>
                  <textarea
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="Enter description..."
                    rows={3}
                    className="w-full px-3 py-2 border"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                      Type
                    </label>
                    <select
                      value={newType}
                      onChange={(e) => setNewType(e.target.value)}
                      className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border"
                    >
                      <option value="">Select type...</option>
                      <option value="problem">Problem</option>
                      <option value="opportunity">Opportunity</option>
                      <option value="risk">Risk</option>
                      <option value="solution">Solution</option>
                      <option value="commitment">Commitment</option>
                      <option value="investment">Investment</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                      Altitude
                    </label>
                    <select
                      value={newAltitude ?? ''}
                      onChange={(e) => setNewAltitude(e.target.value ? Number(e.target.value) : undefined)}
                      className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border"
                    >
                      <option value="">Select altitude...</option>
                      <option value="1">1 - Task</option>
                      <option value="2">2 - Story</option>
                      <option value="3">3 - Feature</option>
                      <option value="4">4 - Epic</option>
                      <option value="5">5 - Initiative</option>
                    </select>
                  </div>
                </div>
                
                {/* Homogeneity hint */}
                {homogeneityResult && homogeneityResult.isHomogeneous && (
                  <div className="p-3 bg-[var(--slate-dim)] border border-[var(--slate)]">
                    <div className="text-sm text-[var(--text-sec)]">
                      <strong>Tip:</strong> For best results, match the existing items:
                      {homogeneityResult.dominantType && (
                        <span className="ml-1">Type: {homogeneityResult.dominantType}</span>
                      )}
                      {homogeneityResult.dominantAltitude !== undefined && (
                        <span className="ml-1">| Altitude: L{homogeneityResult.dominantAltitude}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="p-4 border-t flex justify-end gap-2">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCandidate}
                  disabled={!newTitle.trim()}
                  className="px-4 py-2 text-white bg-[var(--green)] hover:bg-[var(--green)] disabled:opacity-50"
                >
                  Create & Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExerciseItemsPanel;
